import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
    Box,
    ArrowRight,
    ChevronRight,
    BarChart2,
    Menu,
    X,
    Check,
    Star,
    Shield,
    Laptop,
    Loader2,
    Sparkles,
    Home,
    ShoppingBag,
    Facebook,
    MessageCircle,
    X as XIcon,
    Play,
    FileText
} from 'lucide-react';

const NavLink = ({ href, children, isMobile = false }) => (
    <a
        onClick={(e) => {
            e.preventDefault();
            const element = document.querySelector(href);
            if (element) {
                const navHeight = 80;
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - navHeight;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth"
                });
            }

        }}
        href={href}
        className={`relative group cursor-pointer font-medium ${isMobile
            ? 'block w-full py-2.5 text-base text-center'
            : 'inline-block px-3 py-1.5'
            }`}
    >
        <span className="relative z-10 text-gray-600 group-hover:text-indigo-600 transition-colors duration-300">
            {children}
        </span>
        {!isMobile && (
            <span className="absolute inset-0 bg-indigo-50 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 ease-out" />
        )}
    </a>
);

const FeatureTab = ({ active, children, onClick, icon, isLoading }) => (
    <button
        onClick={onClick}
        className={`relative overflow-hidden flex items-center justify-between px-4 py-3 min-h-[3rem] rounded-lg transition-all duration-300 ${active ? 'text-indigo-600' : 'text-gray-600 hover:text-indigo-600'
            }`}
    >
        <span className={`absolute inset-0 bg-indigo-50 transition-transform duration-300 ${active ? 'scale-100' : 'scale-0 group-hover:scale-100'
            }`} />

        <span className="relative flex items-center gap-2.5">
            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                {icon}
            </span>
            <span className="text-sm font-medium">
                {children}
            </span>
            {active && (
                <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                    {isLoading ? (
                        <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
                    ) : (
                        <Sparkles className="w-4 h-4 text-indigo-400 animate-pulse" />
                    )}
                </span>
            )}
        </span>
    </button>
);

const PricingCard = ({ title, price, features, isPopular, period = "/ខែ" }) => (
    <div className={`relative rounded-xl p-6 transition-all duration-300 flex-shrink-0 w-72 ${isPopular
        ? 'bg-gradient-to-b from-indigo-600 to-indigo-800 text-white'
        : 'bg-white text-gray-900 hover:shadow-lg'
        }`}>
        <div className={`font-medium text-base mb-2 ${isPopular ? 'text-white' : 'text-indigo-600'}`}>
            {title}
        </div>
        <div className="flex items-baseline gap-1.5 mb-4">
            <span className="text-2xl font-bold">{price}</span>
            {price !== 'Custom' && (
                <span className={`text-sm ${isPopular ? 'text-indigo-200' : 'text-gray-500'}`}>
                    {period}
                </span>
            )}
        </div>
        <ul className="space-y-3 mb-4 text-sm">
            {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                    {feature === "បង្កើតម៉ឺនុយឌីជីថល" && title === "Starter" ? (
                        <X className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPopular ? 'text-white' : 'text-red-500'}`} />
                    ) : (
                        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPopular ? 'text-white' : 'text-indigo-600'}`} />
                    )}
                    <span className={isPopular ? 'text-indigo-100' : 'text-gray-600'}>{feature}</span>
                </li>
            ))}
        </ul>
    </div>
);

// Let's add a utility function for preloading images
const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = reject;
    });
};

// Function to preload both static and animated versions of a feature
const preloadFeature = (feature) => {
    const promises = [];
    
    // Preload the static image if available
    if (feature.staticSrc) {
        promises.push(preloadImage(`/${feature.staticSrc}`).catch(() => {
            // Silently handle preload errors
            console.log(`Preloading static image for ${feature.id} failed silently`);
        }));
    }
    
    // Preload the GIF
    promises.push(preloadImage(`/${feature.src}`).catch(() => {
        // Silently handle preload errors
        console.log(`Preloading GIF for ${feature.id} failed silently`);
    }));
    
    return Promise.all(promises);
};

const FeaturePreview = ({ feature, isActive, onLoadComplete }) => {
    const [loaded, setLoaded] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);
    const [staticLoaded, setStaticLoaded] = useState(false);
    const [loadProgress, setLoadProgress] = useState(0);
    const imageRef = useRef(null);
    const staticImageRef = useRef(null);

    useEffect(() => {
        if (isActive) {
            setLoaded(false);
            setFadeIn(false);
            setStaticLoaded(false);
            setLoadProgress(0);
            
            // If image is already cached, it might be loaded before onLoad triggers
            if (imageRef.current?.complete) {
                setLoaded(true);
                setLoadProgress(100);
                onLoadComplete(feature.id);
                // Add small delay before showing fade in animation
                setTimeout(() => {
                    setFadeIn(true);
                }, 50);
            }
        }
    }, [isActive, feature.id]);

    // Set up progress tracking
    useEffect(() => {
        if (!isActive) return;

        const loadImage = new Image();
        let progressInterval;

        loadImage.onloadstart = () => {
            setLoadProgress(0);
        };

        // Real progress tracking for browsers that support it
        loadImage.onprogress = (event) => {
            if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                setLoadProgress(progress);
            }
        };

        loadImage.onload = () => {
            setLoadProgress(100);
            clearInterval(progressInterval);
        };

        loadImage.onerror = () => {
            clearInterval(progressInterval);
        };

        // Some browsers/situations may not fire progress events, 
        // so we'll simulate progress as a fallback
        progressInterval = setInterval(() => {
            setLoadProgress(prev => {
                // Simulate progressive loading that slows down as it approaches 90%
                if (prev < 90) {
                    const increment = (90 - prev) / 10;
                    return Math.min(prev + increment, 90);
                }
                return prev;
            });
        }, 300);

        loadImage.src = `/${feature.src}`;

        return () => {
            clearInterval(progressInterval);
        };
    }, [isActive, feature.id]);

    if (!isActive) return null;

    return (
        <div className="w-full aspect-[3/4] bg-gray-50 rounded-xl overflow-hidden relative">
            {/* Skeleton loader with better visual representation */}
            {!loaded && !staticLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
                    <div className="w-full h-full absolute">
                        <div className="animate-pulse bg-gray-200 h-2/3 w-full"></div>
                        <div className="animate-pulse bg-gray-300 h-8 w-3/4 mt-4 mx-auto rounded"></div>
                        <div className="animate-pulse bg-gray-200 h-4 w-1/2 mt-4 mx-auto rounded"></div>
                        <div className="animate-pulse bg-gray-200 h-4 w-2/3 mt-2 mx-auto rounded"></div>
                    </div>
                    <Loader2 className="w-8 h-8 text-indigo-400 animate-spin z-10" />
                </div>
            )}
            
            {/* Loading Progress Indicator */}
            {!loaded && loadProgress > 0 && loadProgress < 100 && (
                <div className="absolute bottom-0 left-0 right-0 z-20 p-4">
                    <div className="bg-black/60 rounded-lg p-3 backdrop-blur-sm">
                        {/* Progress bar */}
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-gradient-to-r from-indigo-500 to-pink-500 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${loadProgress}%` }}
                            />
                        </div>
                        {/* Progress percentage */}
                        <div className="mt-1 text-center text-white text-xs font-medium">
                            {Math.round(loadProgress)}%
                        </div>
                    </div>
                </div>
            )}
            
            {/* Static image placeholder */}
            {feature.staticSrc && (
                <div 
                    className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
                        !loaded && staticLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                >
                    <img
                        ref={staticImageRef}
                        src={`/${feature.staticSrc}`}
                        alt={`${feature.title} preview`}
                        className="w-full h-full object-cover"
                        onLoad={() => setStaticLoaded(true)}
                    />
                </div>
            )}
            
            {/* GIF image - loads after static image */}
            <div 
                className={`w-full h-full transition-opacity duration-500 ${
                    loaded && fadeIn ? 'opacity-100' : 'opacity-0'
                }`}
            >
                <img
                    ref={imageRef}
                    src={`/${feature.src}`}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                    onLoad={() => {
                        setLoaded(true);
                        setLoadProgress(100);
                        onLoadComplete(feature.id);
                        // Add small delay before showing fade in animation
                        setTimeout(() => {
                            setFadeIn(true);
                        }, 50);
                    }}
                />
            </div>
        </div>
    );
};

// Social Media Popup Component
const SocialMediaPopup = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    
    const handleFacebookClick = () => {
        // TODO: Replace with the actual Facebook link provided by the user
        window.open('https://www.facebook.com/share/1A9JAeUkpM/?mibextid=wwXIfr', '_blank');
    };
    
    const handleTelegramClick = () => {
        // TODO: Replace with the actual Telegram link provided by the user
        window.open('https://t.me/sreynechom', '_blank');
    };
    
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4 transform transition-all" 
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">ទាក់ទងយើងតាមរយៈ</h3>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <XIcon size={20} />
                    </button>
                </div>
                
                <div className="space-y-4 my-6">
                    <button 
                        onClick={handleFacebookClick}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                        <Facebook size={20} />
                        <span>Facebook</span>
                    </button>
                    
                    <button 
                        onClick={handleTelegramClick}
                        className="w-full py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                        <MessageCircle size={20} />
                        <span>Telegram</span>
                    </button>
                    
                    {/* App Store Buttons */}
                    <div className="flex flex-col gap-3 mt-2">
                        <p className="text-sm text-gray-600 font-medium text-center">ឬទាញយក Application</p>
                        <div className="flex justify-center gap-2">
                            <a 
                                href="https://play.google.com/store/apps/details?id=stok.smartinventory.app" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="h-[40px] flex items-center justify-center bg-black rounded-lg overflow-hidden"
                            >
                                <img 
                                    src="/google-play-badge.png" 
                                    alt="Get it on Google Play" 
                                    className="h-[40px]"
                                />
                            </a>
                            <a 
                                href="https://apps.apple.com/kh/app/stok-smart-inventory/id6475927331" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="h-[40px] flex items-center justify-center bg-black rounded-lg overflow-hidden"
                            >
                                <img 
                                    src="/app-store-badge.png" 
                                    alt="Download on the App Store" 
                                    className="h-[40px]"
                                />
                            </a>
                        </div>
                    </div>
                </div>
                
                <p className="text-sm text-gray-500 text-center">
                    ទាក់ទងយើងដើម្បីសាកល្បង 30 ថ្ងៃដោយឥតគិតថ្លៃ
                </p>
            </div>
        </div>
    );
};

// Video Popup Component
const VideoPopup = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    
    // YouTube video ID
    const youtubeVideoId = "f-9wrQInyYc";
    
    // YouTube embed URL
    const videoEmbedUrl = `https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1`;
    
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
            <div 
                className="bg-white rounded-2xl shadow-xl p-4 max-w-md w-full mx-4 transform transition-all" 
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">ការបង្ហាញពីរបៀបប្រើប្រាស់ STOK</h3>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <XIcon size={20} />
                    </button>
                </div>
                
                <div className="relative pt-[177.78%] w-full overflow-hidden rounded-xl bg-gray-100">
                    <iframe 
                        className="absolute inset-0 w-full h-full"
                        src={videoEmbedUrl}
                        title="STOK Demo Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
                
                <div className="mt-4 text-center text-sm text-gray-500">
                    មើលការបង្ហាញពីរបៀបប្រើប្រាស់ STOK ដើម្បីគ្រប់គ្រងអាជីវកម្មរបស់អ្នក
                </div>
            </div>
        </div>
    );
};

const STOKLandingPage = () => {
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeFeature, setActiveFeature] = useState('dashboard');
    const [isScrolled, setIsScrolled] = useState(false);
    const [loadedFeatures, setLoadedFeatures] = useState(new Set());
    const [activeTab, setActiveTab] = useState('monthly');
    const [showPopup, setShowPopup] = useState(false);
    const [showVideoPopup, setShowVideoPopup] = useState(false);
    const featurePreviewRef = useRef(null);
    const [showNavigationHint, setShowNavigationHint] = useState(true);
    // Add a ref for the feature tabs container to allow scrolling to active tab
    const featuresTabsContainerRef = useRef(null);
    // Create refs for each feature tab
    const featureTabRefs = useRef({});
    // Add back touch-related state for swipe detection on the preview section
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    // Minimum swipe distance in pixels
    const minSwipeDistance = 50;

    // Add CSS for animations
    const swipeAnimationStyles = `
        @keyframes swipeLeft {
            0% { transform: translateX(0) rotate(180deg); }
            50% { transform: translateX(-5px) rotate(180deg); }
            100% { transform: translateX(0) rotate(180deg); }
        }
        @keyframes swipeRight {
            0% { transform: translateX(0); }
            50% { transform: translateX(5px); }
            100% { transform: translateX(0); }
        }
        .animate-swipeLeft {
            animation: swipeLeft 1s infinite;
        }
        .animate-swipeRight {
            animation: swipeRight 1s infinite;
        }
    `;

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Hide navigation hint after 5 seconds
    useEffect(() => {
        if (showNavigationHint) {
            const timer = setTimeout(() => {
                setShowNavigationHint(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showNavigationHint]);

    // Add an effect to scroll the active tab into view whenever activeFeature changes
    useEffect(() => {
        // Scroll the active tab into view when activeFeature changes
        if (featuresTabsContainerRef.current && featureTabRefs.current[activeFeature]) {
            const container = featuresTabsContainerRef.current;
            const activeTab = featureTabRefs.current[activeFeature];
            
            // Calculate position to scroll to
            const tabLeft = activeTab.offsetLeft;
            const tabWidth = activeTab.offsetWidth;
            const containerWidth = container.offsetWidth;
            const containerScrollLeft = container.scrollLeft;
            
            // If the tab is not fully visible, scroll to make it visible
            if (tabLeft < containerScrollLeft || tabLeft + tabWidth > containerScrollLeft + containerWidth) {
                container.scrollTo({
                    left: tabLeft - (containerWidth / 2) + (tabWidth / 2),
                    behavior: 'smooth'
                });
            }
        }
    }, [activeFeature]);

    // Preload all feature images in the background for better UX
    useEffect(() => {
        // Start preloading after a slight delay to prioritize current view
        const timer = setTimeout(() => {
            // Preload all feature images except the active one which is already loading
            features.forEach(feature => {
                if (feature.id !== activeFeature && !loadedFeatures.has(feature.id)) {
                    preloadFeature(feature).then(() => {
                        setLoadedFeatures(prev => new Set([...prev, feature.id]));
                    });
                }
            });
        }, 3000); // Wait 3 seconds before starting background preloading
        
        return () => clearTimeout(timer);
    }, []);

    const features = [
        {
            id: 'dashboard',
            icon: <Home className="w-4 h-4" />,
            title: 'ផ្ទាំងខាងមុខ',
            src: 'home.gif',
            staticSrc: 'home-static.jpg',
            description: 'មើលព័ត៌មានសំខាន់ៗទាំងអស់នៅលើផ្ទាំងតែមួយ'
        },
        {
            id: 'reports',
            icon: <BarChart2 className="w-4 h-4" />,
            title: 'របាយការណ៍',
            src: 'sale.gif',
            staticSrc: 'sale-static.jpg',
            description: 'របាយការណ៍លម្អិតនិងការវិភាគទិន្នន័យ'
        },
        {
            id: 'inventory',
            icon: <Box className="w-4 h-4" />,
            title: 'ស្តុក',
            src: 'inventory.gif',
            staticSrc: 'inventory-static.jpg',
            description: 'គ្រប់គ្រងស្តុកទំនិញបានយ៉ាងងាយស្រួល'
        },
        {
            id: 'order',
            icon: <ShoppingBag className="w-4 h-4" />,
            title: 'រៀបចំការកម្មង',
            src: 'order.gif',
            staticSrc: 'order-static.jpg',
            description: 'គ្រប់គ្រងស្តុកទំនិញបានយ៉ាងងាយស្រួល'
        },
        {
            id: 'digital_menu',
            icon: <FileText className="w-4 h-4" />,
            title: 'ម៉ឺនុយឌីជីថល',
            src: 'STOKShare.gif',
            staticSrc: 'STOKShare-static.jpg',
            description: 'បង្កើតនិងចែករំលែកម៉ឺនុយឌីជីថលយ៉ាងងាយស្រួល'
        },
       
    ];

    const pricingPlans = {
        monthly: [
            {
                title: "Starter",
                price: "$9.90",
                period: "/ខែ",
                features: [
                    "ការគ្រប់គ្រងស្តុកមូលដ្ឋាន",
                    "អ្នកប្រើប្រាស់ ១ នាក់",
                    "ស្តុកគ្មានដែនកំណត់",
                    "ទំនិញ ១០០០​​​​​​​ មុខ",
                    "បង្កើតម៉ឺនុយឌីជីថល"
                ]
            },
            {
                title: "Standard",  
                price: "$14.90",
                period: "/ខែ",
                isPopular: true,
                features: [
                    "មុខងារ Starter ទាំងអស់",
                    "អ្នកប្រើប្រាស់ ១០ នាក់",
                    "ស្តុកគ្មានដែនកំណត់",
                    "ទំនិញ ៥០០០ មុខ",
                    "បង្កើតម៉ឺនុយឌីជីថល"
                ]
            },
            {
                title: "Standard​ Plus",
                price: "$29.90",
                features: [
                    "មុខងារ Standard ទាំងអស់",
                    "អ្នកប្រើប្រាស់ ១០០ នាក់",
                    "ស្តុកគ្មានដែនកំណត់",
                    "ទំនិញ ១០០០០ មុខ",
                    "បង្កើតម៉ឺនុយឌីជីថល"
                ]
            }
        ],
        yearly: [
            {
                title: "Starter",
                price: "$99",
                period: "/ឆ្នាំ",
                features: [
                    "ការគ្រប់គ្រងស្តុកមូលដ្ឋាន",
                    "អ្នកប្រើប្រាស់ ១ នាក់",
                    "ស្តុកគ្មានដែនកំណត់",
                    "ទំនិញ ១០០០​​​​​​​ មុខ",
                    "បង្កើតម៉ឺនុយឌីជីថល",
                    "សន្សំប្រាក់ជាង $20",
                ]
            },
            {
                title: "Standard",
                price: "$149",
                period: "/ឆ្នាំ",
                isPopular: true,
                features: [
                    "មុខងារ Starter ទាំងអស់",
                    "អ្នកប្រើប្រាស់ ១០ នាក់",
                    "ស្តុកគ្មានដែនកំណត់",
                    "ទំនិញ ៥០០០ មុខ",
                    "បង្កើតម៉ឺនុយឌីជីថល",
                    "សន្សំប្រាក់ជាង $30",
                ]
            },
            {
                title: "Standard​ Plus",
                price: "$299",
                period: "/ឆ្នាំ",
                features: [
                    "មុខងារ Standard ទាំងអស់",
                    "អ្នកប្រើប្រាស់ ១០០ នាក់",
                    "ស្តុកគ្មានដែនកំណត់",
                    "ទំនិញ ១០០០០ មុខ",
                    "បង្កើតម៉ឺនុយឌីជីថល",
                    "សន្សំប្រាក់ជាង $60",
                ]
            }
        ]
    };


    const [isScrolling, setIsScrolling] = useState(false);

    // Pause animation on user interaction
    const handleUserInteraction = () => {
        setIsScrolling(true);
    };

    // Reset the scroll state after user interaction stops for a while
    useEffect(() => {
        let timer;
        if (isScrolling) {
            timer = setTimeout(() => {
                setIsScrolling(false);
            }, 600000);
        }

        return () => clearTimeout(timer); // Cleanup on component unmount or state change
    }, [isScrolling]);

    // Handle image load completion
    const handleLoadComplete = (featureId) => {
        setLoadedFeatures(prev => new Set([...prev, featureId]));
        
        // Preload next feature's image after current one loads
        const currentIndex = features.findIndex(f => f.id === featureId);
        const nextIndex = (currentIndex + 1) % features.length;
        const nextFeature = features[nextIndex];
        
        // Preload the next image
        if (nextFeature && !loadedFeatures.has(nextFeature.id)) {
            preloadFeature(nextFeature).then(() => {
                setLoadedFeatures(prev => new Set([...prev, nextFeature.id]));
            });
        }
    };

    // Handle touch start event
    const onTouchStart = (e) => {
        setTouchEnd(null); // Reset touch end on touch start
        setTouchStart(e.targetTouches[0].clientX);
    };

    // Handle touch move event
    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    // Handle touch end event
    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        
        if (isLeftSwipe) {
            // Swiped from right to left - show next feature
            switchFeature('left');
        } else if (isRightSwipe) {
            // Swiped from left to right - show previous feature
            switchFeature('right');
        }
    };

    // Feature preview section
    const renderFeaturePreview = () => (
        <div 
            id="features" 
            className="relative max-w-4xl mx-auto pt-16"
            ref={featurePreviewRef}
        >
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl transform rotate-1 scale-105 opacity-10 blur-lg animate-pulse" />

            <div 
                className="relative bg-white rounded-2xl shadow-xl p-4 md:p-6 border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
                // Removing touch handlers from the whole container
            >
                {/* Scrollable Feature Tabs */}
                <div
                    className="overflow-x-auto scrollbar-hide -mx-4 px-4 mb-6"
                    onClick={handleUserInteraction}
                    onWheel={handleUserInteraction}
                    onScroll={handleUserInteraction}
                    ref={featuresTabsContainerRef}
                >
                    <div className={`flex min-w-max gap-3 ${isScrolling ? "" : "animate-scrollFeatures"}`}>
                        {features.map((feature) => (
                            <div 
                                key={feature.id} 
                                className="flex-shrink-0 px-0.5"
                                ref={el => featureTabRefs.current[feature.id] = el}
                            >
                                <FeatureTab
                                    active={activeFeature === feature.id}
                                    onClick={() => {
                                        setActiveFeature(feature.id);
                                        if (!loadedFeatures.has(feature.id)) {
                                            setLoadedFeatures(prev => {
                                                const newSet = new Set(prev);
                                                newSet.delete(feature.id);
                                                return newSet;
                                            });
                                        }
                                    }}
                                    icon={feature.icon}
                                    isLoading={activeFeature === feature.id && !loadedFeatures.has(feature.id)}
                                >
                                    {feature.title}
                                </FeatureTab>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Feature Description */}
                <div className="text-sm text-gray-600 mb-6 text-center px-2">
                    {features.find(f => f.id === activeFeature)?.description}
                </div>

                {/* Feature Preview */}
                <div 
                    className="w-full max-w-lg mx-auto relative"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    <FeaturePreview
                        feature={features.find(f => f.id === activeFeature)}
                        isActive={true}
                        onLoadComplete={handleLoadComplete}
                    />
                    
                    {/* Remove Swipe Gesture Hint since we're disabling swipe behavior */}
                    {/* Only show hint for the navigation arrows instead */}
                    {showNavigationHint && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="bg-black/60 text-white px-4 py-2 rounded-lg backdrop-blur-sm flex items-center space-x-2 animate-pulse">
                                <span className="text-sm font-medium">អូសដើម្បីផ្លាស់ប្តូរមុខងារ</span>
                                <div className="flex items-center">
                                    <ChevronRight className="w-4 h-4 transform rotate-180 animate-swipeLeft" />
                                    <ChevronRight className="w-4 h-4 animate-swipeRight" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Swipe Navigation Indicators */}
                <div className="flex justify-between items-center mt-4">
                    <button 
                        onClick={() => switchFeature('right')} 
                        className="p-2 rounded-full bg-gray-100 hover:bg-indigo-100 transition-colors"
                        aria-label="Previous Feature"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-500 transform rotate-180" />
                    </button>
                    
                    <div className="flex space-x-1">
                        {features.map((feature, index) => (
                            <div 
                                key={index}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                    activeFeature === feature.id 
                                        ? 'bg-indigo-600 scale-125' 
                                        : 'bg-gray-300'
                                }`}
                            />
                        ))}
                    </div>
                    
                    <button 
                        onClick={() => switchFeature('left')} 
                        className="p-2 rounded-full bg-gray-100 hover:bg-indigo-100 transition-colors"
                        aria-label="Next Feature"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
            </div>
        </div>
    );

    const handleStartButtonClick = () => {
        setShowPopup(true);
    };
    
    const handleDemoButtonClick = () => {
        setShowVideoPopup(true);
    };

    // Switch to the next or previous feature based on swipe direction
    const switchFeature = (direction) => {
        const currentIndex = features.findIndex(f => f.id === activeFeature);
        let newIndex;

        if (direction === 'right') {
            // Move to previous feature (swipe right)
            newIndex = currentIndex - 1;
            if (newIndex < 0) newIndex = features.length - 1;
        } else {
            // Move to next feature (swipe left)
            newIndex = currentIndex + 1;
            if (newIndex >= features.length) newIndex = 0;
        }

        setActiveFeature(features[newIndex].id);
        handleUserInteraction();
    };

    // Check if we're on the main landing page - more precise check
    const isMainLanding = location.pathname === '/' || location.pathname === '';
    const hostname = window.location.hostname;
    const fullPath = window.location.pathname;
    
    useEffect(() => {
      // For debugging path detection
      console.log("Current path:", fullPath);
      console.log("Is main landing:", isMainLanding);
    }, [fullPath, isMainLanding]);

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>STOK - ប្រព័ន្ធគ្រប់គ្រងស្តុកឆ្លាតវៃ</title>
                <meta name="description" content="ប្រព័ន្ធគ្រប់គ្រងស្តុកឆ្លាតវៃ សម្រាប់អាជីវកម្មសម័យថ្មី។ គ្រប់គ្រងស្តុកទំនិញរបស់អ្នកបានគ្រប់ពេលវេលា និងគ្រប់ទីកន្លែង។" />
                <meta name="keywords" content="inventory management, stock control, POS, point of sale, Cambodia, Khmer, business management" />
                <link rel="canonical" href="https://stokkh.com" />
                
                {/* Only add these Open Graph tags for the main landing page */}
                {isMainLanding && (
                  <>
                    {/* Open Graph / Facebook */}
                    <meta property="og:type" content="website" />
                    <meta property="og:url" content="https://stokkh.com" />
                    <meta property="og:title" content="STOK - ស្តុក" />
                    <meta property="og:description" content="ប្រព័ន្ធគ្រប់គ្រងស្តុកឆ្លាតវៃ សម្រាប់អាជីវកម្មសម័យថ្មី។ គ្រប់គ្រងស្តុកទំនិញរបស់អ្នកបានគ្រប់ពេលវេលា និងគ្រប់ទីកន្លែង។" />
                    <meta property="og:image" content="https://stokkh.com/logo.png" />
                    
                    {/* Twitter */}
                    <meta property="twitter:card" content="summary_large_image" />
                    <meta property="twitter:url" content="https://stokkh.com" />
                    <meta property="twitter:title" content="STOK - ស្តុក" />
                    <meta property="twitter:description" content="ប្រព័ន្ធគ្រប់គ្រងស្តុកឆ្លាតវៃ សម្រាប់អាជីវកម្មសម័យថ្មី។ គ្រប់គ្រងស្តុកទំនិញរបស់អ្នកបានគ្រប់ពេលវេលា និងគ្រប់ទីកន្លែង។" />
                    <meta property="twitter:image" content="https://stokkh.com/logo.png" />
                  </>
                )}
                
                {/* JSON-LD structured data */}
                <script type="application/ld+json">
                {`
                    {
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "STOK",
                        "description": "ប្រព័ន្ធគ្រប់គ្រងស្តុកឆ្លាតវៃ សម្រាប់អាជីវកម្មសម័យថ្មី។ គ្រប់គ្រងស្តុកទំនិញរបស់អ្នកបានគ្រប់ពេលវេលា និងគ្រប់ទីកន្លែង។",
                        "applicationCategory": "BusinessApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "9.90",
                            "priceCurrency": "USD"
                        },
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.8",
                            "reviewCount": "124"
                        }
                    }
                `}
                </script>
            </Helmet>
            
            {/* Apply swipe animation styles */}
            <style dangerouslySetInnerHTML={{ __html: swipeAnimationStyles }} />
            
            {/* Navbar */}
            <nav className={`fixed w-full z-40 transition-all duration-500 ${isScrolled
                ? 'bg-white/90 backdrop-blur-xl shadow-lg'
                : 'bg-transparent'
                }`}>
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo */}
                        <div className="relative group cursor-pointer">
                            <div className="absolute -inset-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl opacity-0 group-hover:opacity-70 blur transition-all duration-500" />
                            <div className="relative flex items-center space-x-3 bg-white px-4 py-2 rounded-xl">
                                <div className="relative">
                                    <div className="h-10 w-10 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                                        {/* <Box className="w-6 h-6 text-white" /> */}
                                        <img className="rounded-lg h-10 w-10" src="/logo.png" alt="logo" />
                                    </div>
                                </div>
                                <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
                                    STOK
                                </span>
                            </div>
                        </div>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center space-x-4">
                            <NavLink href="#features">មុខងារ</NavLink>
                            <NavLink href="#pricing">តម្លៃ</NavLink>
                            <button 
                                className="px-6 py-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300"
                                onClick={handleStartButtonClick}
                            >
                                ចាប់ផ្តើម
                            </button>
                        </div>

                        {/* Mobile Menu Button */}
                        {/* Mobile Menu Button */}
                        <button
                            className="md:hidden relative w-12 h-12 flex items-center justify-center z-50"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            <span className="absolute inset-0 bg-gray-100 rounded-lg opacity-0 transition-opacity hover:opacity-100" />
                            <span className="relative text-gray-600 pointer-events-none">
                                {isMenuOpen ? <X /> : <Menu />}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                <div
                    className={`md:hidden fixed inset-x-0 top-20 bg-white/95 backdrop-blur-2xl shadow-xl transform transition-all duration-300 ease-out ${isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
                        }`}
                >
                    <div className="p-4 space-y-4">
                        <NavLink href="#features" isMobile>មុខងារ</NavLink>
                        <NavLink href="#pricing" isMobile>តម្លៃ</NavLink>
                        <button 
                            className="w-full py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-lg font-medium"
                            onClick={handleStartButtonClick}
                        >
                            ចាប់ផ្តើម
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-16 px-4 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(129,140,248,0.1),transparent_50%)]" />
                <div className="container mx-auto relative">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
                            គ្រប់គ្រង
                            <span className="relative mx-2 inline-block text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 leading-[2]">
                                ស្តុក
                            </span>
                            និង
                            <span className="relative mx-2 inline-block text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 leading-[2]">
                                ការលក់
                            </span>
                            កាន់តែរងាយស្រួលនិងទំនើប
                        </h1>

                        <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                            ប្រព័ន្ធគ្រប់គ្រងស្តុកឆ្លាតវៃ សម្រាប់អាជីវកម្មសម័យថ្មី។ គ្រប់គ្រងស្តុកទំនិញរបស់អ្នកបានគ្រប់ពេលវេលា និងគ្រប់ទីកន្លែង។</p>

                        {/* CTA Buttons - improved for small screens */}
                        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8">
                            {/* Gradient Button */}
                            <button 
                                className="group relative w-full sm:w-auto overflow-hidden bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 rounded-xl shadow-md hover:shadow-xl transition-all whitespace-nowrap"
                                onClick={handleStartButtonClick}
                            >
                                <span className="relative z-10 flex items-center justify-center font-medium">
                                    សាកល្បងឥតគិតថ្លៃ
                                    <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>

                            {/* Outlined Button */}
                            <button 
                                className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-xl border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 transition-all font-medium whitespace-nowrap"
                                onClick={handleDemoButtonClick}
                            >
                                <span className="flex items-center justify-center">
                                    មើលការបង្ហាញ
                                    <ChevronRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                                </span>
                            </button>
                        </div>

                        {/* App Download Buttons */}
                        <div className="flex flex-wrap justify-center gap-4 mb-8">
                            <a 
                                href="https://play.google.com/store/apps/details?id=stok.smartinventory.app" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="h-[48px] flex items-center justify-center bg-black rounded-lg overflow-hidden"
                            >
                                <img 
                                    src="/google-play-badge.png" 
                                    alt="Get it on Google Play" 
                                    className="h-[48px]"
                                />
                            </a>
                            <a 
                                href="https://apps.apple.com/kh/app/stok-smart-inventory/id6475927331" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="h-[48px] flex items-center justify-center bg-black rounded-lg overflow-hidden"
                            >
                                <img 
                                    src="/app-store-badge.png" 
                                    alt="Download on the App Store" 
                                    className="h-[48px]"
                                />
                            </a>
                        </div>
                    </div>

                    {/* Feature Preview with scrollable tabs on mobile */}
                    {renderFeaturePreview()}

                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
                <div className="container mx-auto">
                    {/* Pricing Header */}
                    <div className="text-center mb-12">
                        <span className="inline-block px-6 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-full text-indigo-600 text-xl font-medium mb-4">
                            តម្លៃសមរម្យ
                        </span>
                        <p className="text-gray-600 max-w-2xl mx-auto bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
                            ជ្រើសរើសគម្រោងដែលសមស្របសម្រាប់អាជីវកម្មរបស់អ្នក។ អាចផ្លាស់ប្តូរគម្រោងបានគ្រប់ពេល។
                        </p>
                    </div>

                    {/* Pricing Toggle */}
                    <div className="flex justify-center items-center space-x-4 mb-8">
                        <span className={`text-sm ${activeTab === 'monthly' ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
                            បង់ប្រចាំខែ
                        </span>
                        <button
                            onClick={() => setActiveTab(activeTab === 'monthly' ? 'yearly' : 'monthly')}
                            className="relative w-14 h-7 bg-indigo-600 rounded-full transition-colors duration-300"
                        >
                            <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform duration-300 transform ${activeTab === 'yearly' ? 'translate-x-7' : ''
                                }`} />
                        </button>
                        <span className={`text-sm ${activeTab === 'yearly' ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
                            បង់ប្រចាំឆ្នាំ
                            <span className="ml-1 text-xs text-green-500 font-medium">
                                (សន្សំដល់ ២៥%)
                            </span>
                        </span>
                    </div>

                    {/* Pricing Cards Container - Mobile scroll, Desktop centered */}
                    <div className="md:flex md:justify-center">
                        <div className="flex gap-6 md:gap-6 overflow-x-auto md:overflow-x-visible px-4 pb-4 md:pb-0 snap-x md:snap-none scrollbar-hide -mx-4 md:mx-0 md:flex-row md:justify-center">
                            {pricingPlans[activeTab].map((plan, index) => (
                                <PricingCard
                                    key={index}
                                    {...plan}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="relative py-24 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.15),transparent_70%)] animate-pulse" />
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'white\' fill-opacity=\'0.05\'%3E%3Ccircle cx=\'3\' cy=\'3\' r=\'1\'/%3E%3C/g%3E%3C/svg%3E")',
                        backgroundSize: '20px 20px'
                    }} />
                </div>
                <div className="relative container mx-auto px-4 text-center">
                    <div className="max-w-2xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                            ចាប់ផ្តើមប្រើប្រាស់ STOK ថ្ងៃនេះ
                        </h2>
                        <p className="text-xl mb-8 text-indigo-100">
                            សាកល្បងដោយឥតគិតថ្លៃរយៈពេល ៣០ ថ្ងៃ
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                            <button 
                                className="group relative overflow-hidden bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all inline-flex items-center"
                                onClick={handleStartButtonClick}
                            >
                                <span className="relative z-10 flex items-center">
                                    ចាប់ផ្តើមឥឡូវនេះ
                                    <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                                </span>
                            </button>
                        </div>
                        <div className="flex flex-wrap justify-center gap-4">
                            <a 
                                href="https://play.google.com/store/apps/details?id=stok.smartinventory.app" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="h-[48px] flex items-center justify-center bg-black rounded-lg overflow-hidden"
                            >
                                <img 
                                    src="/google-play-badge.png" 
                                    alt="Get it on Google Play" 
                                    className="h-[48px]"
                                />
                            </a>
                            <a 
                                href="https://apps.apple.com/kh/app/stok-smart-inventory/id6475927331" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="h-[48px] flex items-center justify-center bg-black rounded-lg overflow-hidden"
                            >
                                <img 
                                    src="/app-store-badge.png" 
                                    alt="Download on the App Store" 
                                    className="h-[48px]"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-12 px-4">
                <div className="container mx-auto max-w-5xl">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="group flex items-center space-x-3">
                            <div className="relative">
                                <div className="absolute -inset-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl opacity-0 group-hover:opacity-70 blur transition-all duration-500" />
                                <div className="relative h-10 w-10 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                                    <img src="/logo.png" alt="STOK Logo" className="rounded-lg h-10 w-10" />
                                </div>
                            </div>
                            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                                STOK
                            </span>
                        </div>
                        <div className="flex flex-wrap justify-center gap-6">
                            <a href="#" className="hover:text-white transition-colors hover:underline decoration-indigo-500">គោលការណ៍</a>
                            <a href="#" className="hover:text-white transition-colors hover:underline decoration-indigo-500">លក្ខខណ្ឌ</a>
                            <a href="#" onClick={(e) => { e.preventDefault(); handleStartButtonClick(); }} className="hover:text-white transition-colors hover:underline decoration-indigo-500">ទំនាក់ទំនង</a>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
                        <p>&copy; {new Date().getFullYear()} STOK. រក្សាសិទ្ធិគ្រប់យ៉ាង</p>
                        <div className="mt-6 flex justify-center items-center space-x-4">
                            <div className="flex items-center space-x-2 text-sm">
                                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                                <span>ប្រព័ន្ធកំពុងដំណើរការល្អ</span>
                            </div>
                            <a href="#" className="text-sm hover:text-white transition-colors">
                                ស្ថានភាពប្រព័ន្ធ
                            </a>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Social Media Popup */}
            {showPopup && (
                <SocialMediaPopup
                    isOpen={showPopup}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {/* Video Popup */}
            {showVideoPopup && (
                <VideoPopup
                    isOpen={showVideoPopup}
                    onClose={() => setShowVideoPopup(false)}
                />
            )}
        </div>
    );
};

export default STOKLandingPage;